import { Empty } from 'antd';
import { Card } from '../../design-system';
import ChartResults from '../chart/chartResults';
import { LazyMount } from '../chart/LazyMount';
import { CurveSeries, TargetRockPage4View } from '../../@types/ghePage4/types';
import styles from './GhePage4TopSummaryCharts.module.css';

type Props = {
  rocks: TargetRockPage4View[];
};

const toChartSeries = (
  label: string,
  curve: CurveSeries,
  dashStyle?: string
) => ({
  name: label,
  dashStyle,
  data: curve.x_values.map((x, index) => ({
    x,
    y: curve.y_values[index]
  }))
});

export const GhePage4TopSummaryCharts = ({ rocks }: Props) => {
  const fixedPlotMargins = {
    chartMarginLeft: 72,
    chartMarginRight: 18,
    chartMarginTop: 56,
    chartMarginBottom: 70
  };

  const drainageSeries = rocks
    .map(rock => {
      const curve = rock.summary_curves?.drainage_fractional_flow;
      if (!curve) return null;
      return toChartSeries(`${rock.rock_id}`, curve, 'ShortDash');
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const imbibitionSeries = rocks
    .map(rock => {
      const curve = rock.summary_curves?.imbibition_fractional_flow;
      if (!curve) return null;
      return toChartSeries(`${rock.rock_id}`, curve, 'ShortDash');
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const avgPcSeries = rocks
    .map(rock => {
      const curve = rock.summary_curves?.average_capillary_pressure;
      if (!curve) return null;
      return toChartSeries(`${rock.rock_id}`, curve, 'ShortDash');
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  const hasAnySeries =
    drainageSeries.length > 0 ||
    imbibitionSeries.length > 0 ||
    avgPcSeries.length > 0;

  if (!hasAnySeries) {
    return (
      <Card fullWidth padding="md" data-testid="ghe-page4-top-summary">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No best-fit summary data available."
        />
      </Card>
    );
  }

  const summaryChart = (
    title: string,
    yAxis: string,
    data: ReturnType<typeof toChartSeries>[],
    logarithmic = false
  ) => (
    <LazyMount key={title} style={{ minHeight: 280 }}>
      <ChartResults
        containerWidth="100%"
        height="280px"
        title={title}
        xAxis="Water Saturation"
        yAxis={yAxis}
        xAxisMin={0}
        xAxisMax={1}
        yAxisType={logarithmic ? 'logarithmic' : 'linear'}
        showLegend={false}
        backgroundColor="var(--ds-color-white)"
        borderColor="var(--ds-emerald-zinc-100)"
        marginTop={0}
        {...fixedPlotMargins}
        data={data}
      />
    </LazyMount>
  );

  return (
    <div data-testid="ghe-page4-top-summary" className={styles.stack}>
      {summaryChart('Drainage Fractional Flow', 'Fractional Flow', drainageSeries)}
      {summaryChart('Imbibition Fractional Flow', 'Fractional Flow', imbibitionSeries)}
      {summaryChart('Average Capillary Pressure', 'pc (Bar)', avgPcSeries, true)}
    </div>
  );
};
